import { z } from 'zod';
import { RowLabelOptionalSchema } from './row-label';

// === Block Types ===

export const BLOCK_TYPES = [
  'fixed_irrigation',
  'pivot',
  'glasshouse',
  'broadacre',
  'fixed_planting',
] as const;

export const BlockType = z.enum(BLOCK_TYPES);
export type BlockType = z.infer<typeof BlockType>;

export const PIVOT_TYPES = [
  'full',
  'half',
  'quarter',
  'threequarter',
  'onethird',
  'twothird',
] as const;

export const PivotType = z.enum(PIVOT_TYPES);
export type PivotType = z.infer<typeof PivotType>;

// === Reusable field groups ===

const numericOptional = z.number().nonnegative().nullish();
const intOptional = z.number().int().nonnegative().nullish();

/** Fields common to ALL block types */
const commonBlockFields = {
  id: z.string().uuid(),
  company_id: z.number().int().positive(),
  block_type: BlockType,
  block_name: z.string().min(1).max(400),
  is_archived: z.boolean().default(false),

  // Dimensions
  dimensions: numericOptional,
  block_width: numericOptional,
  block_length: numericOptional,
  area_size_metres: numericOptional,
  total_ha: numericOptional,
  total_hectare: numericOptional,
  total_block_area: numericOptional,
  total_area: numericOptional,

  // Crop (UUID refs)
  crop_type_id: z.string().uuid().nullish(),
  crop_variety_id: z.string().uuid().nullish(),

  // Rows
  row_numbers: intOptional,
  // start_row is the fixed-irrigation sprinkler seed — a "bay.bed" label
  // ("0.4" anchors numbering at bay 0, bed 4), matching legacy PHP
  // `sprinklernumber()`. Planting/glasshouse pass plain integers — also valid.
  // Accepts a string so the label survives once the column becomes text; do not
  // narrow it back to a number. finish_row is a plain row count, not a label.
  start_row: RowLabelOptionalSchema,
  finish_row: intOptional,
  rows_apart_metres: numericOptional,
  plants_per_ha: numericOptional,

  // Beds & Bays
  bed_width: numericOptional,
  kg_per_ha: numericOptional,
  total_beds: intOptional,
  beds_per_bay: intOptional,
  total_bays: intOptional,
  total_bay_area: numericOptional,
  total_bay_area2: numericOptional,
  total_bed_meter: numericOptional,
  hectres_per_bed: numericOptional,
  length_per_bed: numericOptional,
  total_sub_sectionarea: numericOptional,

  // Sprinklers
  sprinkler_width: numericOptional,
  sprinkler_gaps: numericOptional,
  sprinkler_length: numericOptional,
  sprinkler_apart_metres: numericOptional,
  start_sprinkler_row: intOptional,
  finish_sprinkler_row: intOptional,
  total_gaps: intOptional,
  segment_length: numericOptional,

  // Map / Image
  // Stores just the filename (e.g. `block_20240615_120000.png`) — matches the
  // legacy click2gro click2gro convention. The API resolves filename →
  // public URL of the `blockimages` Supabase bucket on read.
  block_image_url: z.string().nullish(),
  // Transient fields: base64 PNG of the freshly-drawn polygon plus the
  // client-generated filename (e.g. `block_20240615_120000.png`). The API
  // route uploads the bytes to the `blockimages` Supabase bucket keyed by
  // that filename, then writes JUST the filename into `block_image_url`.
  // Mirrors the legacy click2gro `saveBlockImage` convention exactly.
  // Neither field is persisted directly.
  block_image_data_url: z.string().nullish(),
  block_image_filename: z.string().nullish(),
  polygon_lat_long: z.unknown().nullish(),
  polygon_centroid: z.unknown().nullish(),

  // Audit
  created_by: z.string().uuid().nullish(),
  updated_by: z.string().uuid().nullish(),
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
  deleted_at: z.string().datetime().nullish(),
};

/** Planting fields (glasshouse, broadacre, fixed_planting) */
const plantingFields = {
  planting_gaps: numericOptional,
  total_rows: intOptional,
  planting_length: numericOptional,
  planting_width: numericOptional,
  sub_area_total: numericOptional,
};

/** Pivot-specific fields */
const pivotFields = {
  pivot_sub_type: PivotType.nullish(),
  halfpivot_rotation: z.string().nullish(),
  full_pivot_area: numericOptional,
  full_pivot_area_ha: numericOptional,
  full_pivot_area_acres: numericOptional,
  half_pivot_area: numericOptional,
  half_pivot_area_ha: numericOptional,
  half_pivot_area_acres: numericOptional,
  quarter_pivot_area: numericOptional,
  quarter_pivot_area_ha: numericOptional,
  quarter_pivot_area_acres: numericOptional,
  threequarter_pivot_area: numericOptional,
  threequarter_pivot_area_ha: numericOptional,
  threequarter_pivot_area_acres: numericOptional,
  onethird_pivot_area: numericOptional,
  onethird_pivot_area_ha: numericOptional,
  onethird_pivot_area_acres: numericOptional,
  twothird_pivot_area: numericOptional,
  twothird_pivot_area_ha: numericOptional,
  twothird_pivot_area_acres: numericOptional,
};

/** Spray fields (fixed_planting only) */
const sprayFields = {
  water_rate_per_ha: numericOptional,
  total_sprays_require: numericOptional,
  total_tank_per_ha: numericOptional,
  spray_area: numericOptional,
  total_spray_area: numericOptional,
  tank_size: numericOptional,
};

// === Full Block Schema (single table, all fields) ===

export const BlockSchema = z
  .object({
    ...commonBlockFields,
    ...plantingFields,
    ...pivotFields,
    ...sprayFields,
  })
  .refine(
    (data) => {
      if (data.block_type === 'pivot') {
        const areas = [
          data.full_pivot_area_ha,
          data.half_pivot_area_ha,
          data.quarter_pivot_area_ha,
          data.threequarter_pivot_area_ha,
          data.onethird_pivot_area_ha,
          data.twothird_pivot_area_ha,
        ];
        return areas.some((a) => a !== undefined && a !== null && a > 0);
      }
      return true;
    },
    { message: 'Pivot blocks must have at least one pivot area defined' },
  );

export type Block = z.infer<typeof BlockSchema>;

// === Insert / Update schemas ===

export const BlockInsertSchema = z.object({
  ...commonBlockFields,
  ...plantingFields,
  ...pivotFields,
  ...sprayFields,
}).omit({
  id: true,
  // company_id is derived server-side from the JWT auth context — clients
  // must never set it (otherwise a logged-in user could insert into another
  // company). The API route forwards companyId as a separate argument to
  // the service. Same reason for created_by / updated_by.
  company_id: true,
  created_at: true,
  updated_at: true,
  deleted_at: true,
  created_by: true,
  updated_by: true,
});
export type BlockInsert = z.infer<typeof BlockInsertSchema>;

export const BlockUpdateSchema = z.object({
  ...commonBlockFields,
  ...plantingFields,
  ...pivotFields,
  ...sprayFields,
}).partial().required({ id: true })
  // Same security rule as InsertSchema — clients can't move a block to a
  // different company by passing a new company_id in the update payload.
  .omit({ company_id: true });
export type BlockUpdate = z.infer<typeof BlockUpdateSchema>;

// === Block Section Schema ===

export const BlockSectionSchema = z.object({
  id: z.string().uuid(),
  block_id: z.string().uuid(),
  section_name: z.string().min(1),
  section_order: z.number().int().nonnegative().default(0),
  area: numericOptional,
  bed_per_square_metre: numericOptional,
  row_meter: numericOptional,
  // Per-section sprinkler seed — a "bay.bed" label, same as the block's.
  start_row: RowLabelOptionalSchema,
  finish_row: intOptional,
  created_at: z.string().datetime(),
  updated_at: z.string().datetime(),
});
export type BlockSection = z.infer<typeof BlockSectionSchema>;

export const BlockSectionInsertSchema = BlockSectionSchema.omit({
  id: true,
  created_at: true,
  updated_at: true,
});
export type BlockSectionInsert = z.infer<typeof BlockSectionInsertSchema>;

// === List query params ===

export const BlockListQuerySchema = z.object({
  block_type: BlockType.optional(),
  // Query params are always strings — coerce.boolean() treats "false" as true. Use enum transform instead.
  is_archived: z.enum(['true', 'false']).optional().default('false').transform((v) => v === 'true'),
  search: z.string().optional(),
  page: z.coerce.number().int().positive().optional().default(1),
  per_page: z.coerce.number().int().positive().max(100).optional().default(25),
});
export type BlockListQuery = z.infer<typeof BlockListQuerySchema>;
